import { CircularProgress, MenuItem } from "@mui/material";
import Divider from "@mui/material/Divider";
import { useStores } from "api/stores";
import { useAppDispatch } from "app/hooks";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { useState } from "react";
import { NavLink, matchPath, useLocation, useNavigate } from "react-router-dom";
import { setCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { clearTableListings } from "reduxState/tables/tablesSlice";
import { ListResponse } from "types/listing";
import { links } from "utilities/links";
import { StoreSearch } from "./StoreSearch";
import { useStyles } from "./StoreSelect.styles";

export interface Store {
  code: string;
  currency: string;
  name: string;
  storeId: string;
}

const findLinksWithId = () => {
  const idLinks: string[] = [];

  Object.values(links).forEach((link: string | Function) => {
    if (typeof link === "function") {
      const linkString = link();

      if (linkString.includes(":id")) {
        idLinks.push(linkString);
      }
    }
  });

  return idLinks;
};

const shouldRedirectToDashboard = (currentPathname: string): boolean => {
  const pathsWithRedirect = [
    links.tiersList,
    links.addTier,
    links.tiersConfiguration,
    ...findLinksWithId(),
  ];

  return !!pathsWithRedirect.find((path) => matchPath(path, currentPathname));
};

interface Props {
  handleAdditionalMenuOpen: () => void;
  handleAdditionalMenuClose: () => void;
  isDrawerOpen?: boolean;
  currentStore?: string;
}

export const StoreList = ({
  handleAdditionalMenuOpen,
  handleAdditionalMenuClose,
  isDrawerOpen,
  currentStore,
}: Props) => {
  const [nameSearch, setNameSearch] = useState("");
  const { debouncedValue: searchQuery } = useDebounce(nameSearch);
  const classes = useStyles({ isDrawerOpen });
  const { message } = useMessage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { useStoreList } = useStores();
  const { data: storesList } = useStoreList({
    filters: [
      ...(searchQuery
        ? [
            {
              condition: "like",
              query: "name",
              value: searchQuery,
            },
          ]
        : []),
      { condition: "", query: "active", value: true },
    ],

    order: "asc",
    rows: 50,
    sortKey: "name",
  }) as { data: ListResponse };
  const isLoading = !storesList;
  const areResults = storesList?.items?.length > 0;

  return (
    <>
      <StoreSearch
        handleAdditionalMenuOpen={handleAdditionalMenuOpen}
        setNameSearch={(searchPhrase: string) => setNameSearch(searchPhrase)}
        nameSearch={nameSearch}
      />
      <Divider className={classes.divider} />
      <div className={classes.listWrapper}>
        {isLoading ? (
          <span className={classes.loading}>
            <span>{message({ id: "loading" })}...</span>
            <span>
              <CircularProgress size={16} />
            </span>
          </span>
        ) : areResults ? (
          storesList.items.map((item: Store, index: number) => (
            <MenuItem
              className={classes.additionalMenuListItem}
              key={index}
              onClick={() => {
                const isCurrentStoreClicked = item.code === currentStore;
                if (isCurrentStoreClicked) {
                  handleAdditionalMenuClose();

                  return;
                }
                dispatch(clearTableListings());
                dispatch(
                  setCurrentStoreData({
                    code: item.code,
                    currency: item.currency,
                    name: item.name,
                    id: item.storeId,
                  }),
                );
                handleAdditionalMenuClose();
                const shouldRedirect = shouldRedirectToDashboard(pathname);

                if (shouldRedirect) navigate(links.dashboard);
              }}
              data-test-id={item.code}
            >
              {item.name}
              <span className={classes.currency}>{`(${item.currency})`}</span>
            </MenuItem>
          ))
        ) : (
          <MenuItem className={classes.additionalMenuListItem} disabled>
            {message({ id: "noResultsFound" })}
          </MenuItem>
        )}
      </div>
      <div className={classes.linkWrapper}>
        <NavLink
          className={classes.link}
          onClick={handleAdditionalMenuClose}
          to={links.addStore}
          state={{ activeTab: "tenants" }}
          data-test-id="add-store"
        >
          <PlusIcon fontSize="small" /> {message({ id: "addTenant" })}
        </NavLink>
      </div>
    </>
  );
};
